import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { NgapAct, SearchFilters } from '../types/ngap';
import { SimulatorCartItem } from '../features/simulator/types';
import {
  extractLetterKeyFromUrl,
  generateCotationPageMeta,
  generateBreadcrumb
} from '../utils/urlUtils';
import { updatePageMeta, updateJsonLd, generateCotationSchema } from '../utils/seoUtils';
import { trackPageView } from '../utils/googleAnalytics';
import { trackLetterKeyPageViewed } from '../utils/analyticsEvents';

// Components
import ResultsGrid from './ResultsGrid';
import Pagination from './Pagination';
import Breadcrumb from './Breadcrumb';
import SkeletonGrid from './SkeletonGrid';
import AppinesBanner from './AppinesBanner';
import Footer from './Footer';

interface CotationPageProps {
  acts: NgapAct[];
  loading: boolean;
  filters: SearchFilters;
  handleFiltersChange: (filters: SearchFilters) => void;
  darkMode: boolean;
  filteredActs: NgapAct[];
  paginatedActs: NgapAct[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  cartItems?: SimulatorCartItem[];
  onToggleCartItem?: (act: NgapAct) => void;
  displayCotationType?: 'current' | 'july2025' | 'july2026' | 'sept2026' | 'july2027';
  onHashtagClick?: (hashtag: string) => void;
}

const CotationPage: React.FC<CotationPageProps> = ({
  acts,
  loading,
  filters,
  handleFiltersChange,
  darkMode,
  filteredActs,
  paginatedActs,
  totalPages,
  currentPage,
  onPageChange,
  itemsPerPage,
  cartItems = [],
  onToggleCartItem,
  displayCotationType,
  onHashtagClick
}) => {
  const { letter_key } = useParams<{ letter_key: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);
  
  useEffect(() => {
    if (letter_key) {
      const letterKey = extractLetterKeyFromUrl(location.pathname);

      if (letterKey) {
        const meta = generateCotationPageMeta(letterKey);
        updatePageMeta(letterKey, meta);

        const letterKeyActsCount = acts.filter(act => act.lettre_cle?.toUpperCase() === letterKey.toUpperCase()).length;
        const jsonLdSchema = generateCotationSchema(letterKey, letterKeyActsCount);
        updateJsonLd(jsonLdSchema, 'cotation-page-jsonld');

        if (!hasTrackedPageView) {
          trackPageView(`Cotations ${letterKey}`, `/cotations/${letterKey.toLowerCase()}`);
          trackLetterKeyPageViewed(letterKey);
          setHasTrackedPageView(true);
        }

        if (filters.lettre_cle.toUpperCase() !== letterKey.toUpperCase()) {
          handleFiltersChange({
            ...filters,
            lettre_cle: letterKey
          });
        }
      } else if (letter_key.length >= 2 && letter_key.length <= 4) {
        const fallbackLetterKey = letter_key.toUpperCase();

        const meta = generateCotationPageMeta(fallbackLetterKey);
        updatePageMeta(fallbackLetterKey, meta);

        const fallbackActsCount = acts.filter(act => act.lettre_cle?.toUpperCase() === fallbackLetterKey).length;
        const jsonLdSchema = generateCotationSchema(fallbackLetterKey, fallbackActsCount);
        updateJsonLd(jsonLdSchema, 'cotation-page-jsonld');

        if (!hasTrackedPageView) {
          trackPageView(`Cotations ${fallbackLetterKey}`, `/cotations/actes-${fallbackLetterKey.toLowerCase()}`);
          trackLetterKeyPageViewed(fallbackLetterKey);
          setHasTrackedPageView(true);
        }

        if (filters.lettre_cle.toUpperCase() !== fallbackLetterKey.toUpperCase()) {
          handleFiltersChange({
            ...filters,
            lettre_cle: fallbackLetterKey
          });
        }
      }
    }
  }, [letter_key, location.pathname, hasTrackedPageView, filters.lettre_cle, handleFiltersChange, filters]);

  const letterKey = React.useMemo(() => {
    const extractedKey = extractLetterKeyFromUrl(location.pathname);
    if (extractedKey) return extractedKey;

    if (letter_key && letter_key.length >= 2 && letter_key.length <= 4) {
      return letter_key.toUpperCase();
    }

    return null;
  }, [letter_key, location.pathname]);
  
  const breadcrumbItems = letterKey ? generateBreadcrumb(letterKey) : [];

  // Filter acts for this specific letter key
  const letterKeyActs = React.useMemo(() => {
    if (!letterKey || !acts.length) return [];
    
    return acts.filter(act => 
      act.lettre_cle && act.lettre_cle.toUpperCase() === letterKey
    );
  }, [acts, letterKey]);

  // Enhanced error handling for invalid letter key
  if (letter_key && !letterKey) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{
        background: darkMode 
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <AppinesBanner className="md:pt-6" />
        
        <div className="pt-20 md:pt-0 px-4 pb-8">
          <div className="max-w-6xl mx-auto">
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-orange-400' : 'text-orange-500'
              }`}>
                ⚠️
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Lettre clé invalide
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                La lettre clé "{letter_key}" n'est pas reconnue dans le référentiel NGAP.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode 
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* App'Ines Banner */}
      <AppinesBanner className="md:pt-6" />
      
      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb items={breadcrumbItems} darkMode={darkMode} />
          
          <div className="text-center mb-8">
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Cotations {letterKey}
            </h1>
            <p className={`text-lg ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {loading ? 'Chargement...' : `${letterKeyActs.length} acte${letterKeyActs.length > 1 ? 's' : ''} disponible${letterKeyActs.length > 1 ? 's' : ''}`}
            </p>
          </div>

          {loading ? (
            <SkeletonGrid darkMode={darkMode} count={6} />
          ) : letterKeyActs.length === 0 ? (
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                🔍
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Aucun acte trouvé
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Aucun acte de kinésithérapie n'est disponible pour la lettre clé {letterKey}.
              </p>
            </div>
          ) : (
            <>
              <ResultsGrid
                results={paginatedActs}
                cartItems={cartItems}
                onToggleCartItem={onToggleCartItem}
                darkMode={darkMode}
                loading={false}
                displayCotationType={displayCotationType}
                onHashtagClick={onHashtagClick}
              />
              
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  darkMode={darkMode}
                  totalItems={filteredActs.length}
                  itemsPerPage={itemsPerPage}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default React.memo(CotationPage);